import { Component, OnInit, OnDestroy } from '@angular/core';
import { ViewWillEnter } from '@ionic/angular';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { IonicModule, ActionSheetController, ModalController, AlertController } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { UserService } from '../../services/user.service';
import { PlaylistService } from '../../services/playlist.service';
import { UserLookupService } from '../../services/user-lookup.service';
import { DisplayService } from '../../services/display.service';
import { CreatePlaylistModalComponent } from '../../components/create-playlist-modal/create-playlist-modal.component';
import { User } from '../../entities/user.model';
import { Playlist } from '../../entities/playlist.model';
import { LANGUAGE_MAPPINGS } from '../settings/language-mapping';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-grammar-home',
  standalone: true,
  imports: [IonicModule, CommonModule, RouterModule, TranslateModule],
  templateUrl: './grammar-home.page.html',
  styleUrls: ['./grammar-home.page.scss'],
})
export class GrammarHomePage implements OnInit, OnDestroy, ViewWillEnter {
  playlists: Playlist[] = [];
  filteredPlaylists: Playlist[] = [];
  searchQuery = '';
  language = 'en';
  displayNames: Record<string, string> = {};
  showBookmarkedOnly = false;
  darkMode = false;
  showWelcomePopup = false;
  private currentUser: User | null = null;
  private userSub!: Subscription;
  private playlistsSub!: Subscription;
  private readonly guestWelcomeSeenKey = 'playlistsGuestWelcomeSeen';
  private readonly nativeLanguageFlags: Record<string, string> = {
    en: 'US',
    es: 'ES',
    fr: 'FR',
    de: 'DE',
    pt: 'PT',
    ja: 'JP',
    ru: 'RU',
    ar: 'SA',
    hi: 'IN',
    zh: 'CN',
    ur: 'PK',
    bn: 'BD',
    he: 'IL'
  };

  get hasBookmarkedPlaylists(): boolean {
    return (this.currentUser?.pinnedPlaylists?.length ?? 0) > 0;
  }

  get languageDisplayName(): string {
    return this.getLanguageOptions().find(l => l.code === this.language)?.name ?? this.language;
  }

  get nativeLanguageFlag(): string {
    const nativeLanguage = this.currentUser?.nativeLanguage || this.translate.currentLang || 'en';
    const countryCode = this.nativeLanguageFlags[nativeLanguage];
    return countryCode ? this.countryCodeToFlag(countryCode) : '🌐';
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private userService: UserService,
    private playlistService: PlaylistService,
    private userLookup: UserLookupService,
    private displayService: DisplayService,
    private actionSheetCtrl: ActionSheetController,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private translate: TranslateService
  ) {}

  private readonly validTargetLanguages = new Set(['en','es','fr','de','pt','ja','ru','ar','hi','zh','ur','bn','he','uk']);

  /** Keep ?target= in the URL in sync with the displayed language (replaceUrl so it doesn't pollute history). */
  private syncUrlParam(lang: string) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { target: lang },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  ngOnInit() {
    // If ?target= is present, honour it as the initial language
    const urlTarget = this.route.snapshot.queryParamMap.get('target');
    if (urlTarget && this.validTargetLanguages.has(urlTarget)) {
      this.language = urlTarget;
    }

    this.userSub = this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;

      if (user) {
        this.darkMode = user.darkMode ?? false;
        this.showWelcomePopup = false;
        // Only apply saved preference when no explicit ?target= was in the URL
        if (user.targetLanguage && !urlTarget) {
          this.language = user.targetLanguage;
        }
      } else {
        const rawGuestPreferences = localStorage.getItem('guestPreferences');
        if (rawGuestPreferences) {
          try {
            const guestPreferences = JSON.parse(rawGuestPreferences);
            this.darkMode = !!guestPreferences.darkMode;
            if (guestPreferences.targetLanguage && !urlTarget) {
              this.language = guestPreferences.targetLanguage;
            }
            this.displayService.setDarkMode(this.darkMode);
          } catch {
            this.darkMode = false;
          }
        } else {
          this.darkMode = false;
        }

        this.showWelcomePopup = localStorage.getItem(this.guestWelcomeSeenKey) !== 'true';
      }

      this.syncUrlParam(this.language);
      this.loadPlaylists();
    });
  }

  ionViewWillEnter() {
    if (!this.currentUser) {
      const raw = localStorage.getItem('guestPreferences');
      if (raw) {
        try {
          const prefs = JSON.parse(raw);
          if (prefs.darkMode !== undefined && prefs.darkMode !== this.darkMode) {
            this.darkMode = prefs.darkMode;
            this.displayService.setDarkMode(this.darkMode);
          }
          if (prefs.targetLanguage && prefs.targetLanguage !== this.language) {
            this.language = prefs.targetLanguage;
            this.syncUrlParam(prefs.targetLanguage);
            this.loadPlaylists();
          }
        } catch {}
      }
    }
  }

  closeWelcomePopup() {
    this.showWelcomePopup = false;
    localStorage.setItem(this.guestWelcomeSeenKey, 'true');
  }

  ngOnDestroy() {
    this.userSub?.unsubscribe();
    this.playlistsSub?.unsubscribe();
  }

  private loadPlaylists() {
    this.playlistsSub?.unsubscribe();
    this.playlistsSub = this.playlistService.getPlaylists(this.language).subscribe(async playlists => {
      this.playlists = playlists;
      this.applySearch();
      const uids = playlists.map(p => p.createdByUid).filter((u): u is string => !!u);
      if (uids.length) {
        const resolved = await this.userLookup.resolveMany(uids);
        this.displayNames = { ...this.displayNames, ...resolved };
      }
    });
    this.playlistService.seedDefaultPlaylists(this.language).catch(console.error);
  }

  onSearch(event: any) {
    this.searchQuery = event.detail.value ?? '';
    this.applySearch();
  }

  toggleBookmarkedFilter() {
    this.showBookmarkedOnly = !this.showBookmarkedOnly;
    this.applySearch();
  }

  private applySearch() {
    const currentUid = this.currentUser?.uid ?? null;
    const bookmarkedIds = new Set(this.currentUser?.pinnedPlaylists ?? []);

    // Step 1: apply visibility filter
    let visible = this.playlists.filter(p => {
      const vis = p.visibility ?? 'public';
      if (vis === 'public') return true;
      if (vis === 'link' && p.id && bookmarkedIds.has(p.id)) return true;
      return p.createdByUid === currentUid;
    });

    // Step 2: apply bookmarked-only or default (featured + own + bookmarked) filter
    // When actively searching, skip the featured filter so all public playlists are searchable
    const q = this.searchQuery.trim().toLowerCase();
    if (this.showBookmarkedOnly) {
      visible = visible.filter(p => p.id && bookmarkedIds.has(p.id));
    } else if (!q) {
      visible = visible.filter(p =>
        p.featured || p.createdByUid === currentUid || (p.id && bookmarkedIds.has(p.id))
      );
    }

    // Step 3: apply search query
    const sortPlaylists = (items: Playlist[]) => {
      return [...items].sort((a, b) => {
        const aPinnedOrOwned = (a.createdByUid === currentUid) || (!!a.id && bookmarkedIds.has(a.id));
        const bPinnedOrOwned = (b.createdByUid === currentUid) || (!!b.id && bookmarkedIds.has(b.id));

        if (aPinnedOrOwned !== bPinnedOrOwned) {
          return aPinnedOrOwned ? -1 : 1;
        }

        const likesDiff = (b.totalLikes ?? 0) - (a.totalLikes ?? 0);
        if (likesDiff !== 0) {
          return likesDiff;
        }

        return (b.songCount ?? 0) - (a.songCount ?? 0);
      });
    };

    if (!q) {
      this.filteredPlaylists = sortPlaylists(visible);
      return;
    }
    const searched = visible.filter(p =>
      p.name.toLowerCase().includes(q) ||
      (p.description && p.description.toLowerCase().includes(q))
    );
    this.filteredPlaylists = sortPlaylists(searched);
  }

  async openLanguagePicker() {
    const buttons = this.getLanguageOptions().map(lang => ({
      text: lang.name,
      handler: () => {
        this.language = lang.code;
        this.syncUrlParam(lang.code);
        this.saveLanguage();
        this.loadPlaylists();
      }
    }));
    buttons.push({ text: this.translate.instant('PLAYLISTS.CANCEL'), role: 'cancel' } as any);

    const sheet = await this.actionSheetCtrl.create({
      header: this.translate.instant('PLAYLISTS.SELECT_LANGUAGE'),
      buttons
    });
    await sheet.present();
  }

  private getLanguageOptions() {
    const preferredLanguage = this.currentUser?.nativeLanguage || this.translate.currentLang || this.translate.getDefaultLang() || 'en';
    return LANGUAGE_MAPPINGS[preferredLanguage] ?? LANGUAGE_MAPPINGS['en'];
  }

  private async saveLanguage() {
    if (this.currentUser) {
      await this.userService.applyChanges({ ...this.currentUser, targetLanguage: this.language });
    }
  }

  async toggleTheme() {
    this.darkMode = !this.darkMode;
    this.displayService.setDarkMode(this.darkMode);

    if (this.currentUser) {
      await this.userService.applyChanges({ ...this.currentUser, darkMode: this.darkMode });
    }
  }

  navigateToPlaylist(playlist: Playlist) {
    this.router.navigate(['/topic-playlist', this.language, playlist.id]);
  }

  goToBeginning() {
    this.router.navigate(['/beginning']);
  }

  private countryCodeToFlag(countryCode: string): string {
    return countryCode
      .toUpperCase()
      .split('')
      .map(c => String.fromCodePoint(127397 + c.charCodeAt(0)))
      .join('');
  }

  async openCreatePlaylistModal() {
    if (!this.currentUser?.displayName) {
      const alert = await this.alertCtrl.create({
        header: this.translate.instant('PLAYLISTS.SIGN_IN_TITLE'),
        message: this.translate.instant('PLAYLISTS.SIGN_IN_MSG'),
        buttons: [
          {
            text: this.translate.instant('PLAYLISTS.SIGN_UP'),
            handler: () => { this.router.navigate(['/signup']); }
          },
          { text: this.translate.instant('PLAYLISTS.OK'), role: 'cancel' }
        ]
      });
      await alert.present();
      return;
    }

    // Check playlist limit
    const userPlaylistCount = this.playlists.filter(p => p.createdByUid === this.currentUser!.uid).length;
    if (userPlaylistCount >= 10) {
      const alert = await this.alertCtrl.create({
        header: this.translate.instant('PLAYLISTS.LIMIT_TITLE'),
        message: this.translate.instant('PLAYLISTS.LIMIT_MSG'),
        buttons: [this.translate.instant('PLAYLISTS.OK')]
      });
      await alert.present();
      return;
    }

    // Ensure usernames collection has this user's current display name
    if (this.currentUser.uid && this.currentUser.displayName) {
      this.userLookup.writeUsername(this.currentUser.uid, this.currentUser.displayName).catch(console.error);
    }

    const modal = await this.modalCtrl.create({
      component: CreatePlaylistModalComponent,
      componentProps: {
        createdBy: this.currentUser.displayName,
        createdByUid: this.currentUser.uid,
        language: this.language
      }
    });
    await modal.present();
  }
}
